import { useEffect, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { motion } from 'framer-motion';
import PageHero from '@/components/shared/PageHero';
import CountUp from '@/components/shared/CountUp';
import { publicApi } from '@/lib/api';
import { getIcon } from '@/lib/icons';
import { Briefcase, GraduationCap, Users, Building2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import useSEO from '@/hooks/useSEO';

const FEATURES = [
    {
        icon: Briefcase,
        title: 'Job Fairs',
        body: 'Multi-sector job fairs with on-spot interviews across Tamil Nadu & Puducherry.',
    },
    {
        icon: Building2,
        title: 'Employer Network',
        body: '34,000+ active employer connections — IT, BFSI, manufacturing, retail and more.',
    },
    {
        icon: GraduationCap,
        title: 'Career Guidance',
        body: 'One-on-one counseling, aptitude tests, and pathway mapping for students.',
    },
    {
        icon: Users,
        title: 'Skill Upskilling',
        body: 'Digital literacy, soft skills and vocational modules with training partners.',
    },
];

export default function JobPortalPage() {
    const [stats, setStats] = useState([]);
    const navigate = useNavigate();
    useSEO({
        title: 'Employment Services',
        description:
            'VVVSI Trust employment ecosystem: 2.15 lakh+ placements facilitated, 34,000+ connected employers, 13+ job fairs, 200+ career guidance programs. Explore our employment support and CSR sponsorship opportunities.',
    });
    useEffect(() => {
        publicApi.getImpactStats().then(setStats).catch(() => {});
    }, []);

    const employmentStats = stats.filter((s) =>
        ['briefcase', 'building-2', 'graduation-cap', 'calendar-days'].includes(s.icon)
    );

    return (
        <div data-testid="job-portal-page">
            <PageHero
                eyebrow="Employment Services"
                title="Connecting talent with opportunity across India."
                subtitle="VVVSI has facilitated 2.15 lakh+ placements. This page showcases our employment ecosystem — not a job listing board."
            />

            {/* Highlight stats */}
            <section className="section-pad">
                <div className="container-page">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        {employmentStats.map((s) => {
                            const Icon = getIcon(s.icon);
                            return (
                                <Card key={s.id} className="bg-card border-border card-lift">
                                    <CardContent className="p-6 text-center">
                                        <Icon className="h-6 w-6 text-[hsl(var(--accent))] mx-auto mb-3" />
                                        <div className="font-display font-semibold text-3xl text-foreground">
                                            <CountUp
                                                end={s.numeric_value || 0}
                                                suffix={s.suffix || ''}
                                            />
                                        </div>
                                        <div className="mt-1 text-[13px] text-muted-foreground">
                                            {s.label}
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* Ecosystem */}
            <section className="section-pad bg-[hsl(var(--secondary))]/60 relative overflow-hidden">
                <div className="absolute inset-0 grain-bg opacity-60 pointer-events-none" />
                <div className="container-page relative">
                    <div className="max-w-2xl">
                        <div className="flex items-center gap-3 mb-3">
                            <span className="accent-bar" />
                            <span className="text-[hsl(var(--accent))] font-semibold text-xs uppercase tracking-widest">
                                Our Ecosystem
                            </span>
                        </div>
                        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
                            A 360-degree employment support model
                        </h2>
                    </div>
                    <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {FEATURES.map((f, i) => {
                            const Icon = f.icon;
                            return (
                                <motion.div
                                    key={f.title}
                                    initial={{ opacity: 0, y: 10 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.4, delay: i * 0.05 }}
                                    viewport={{ once: true, margin: '-60px' }}
                                >
                                    <Card className="h-full bg-card border-border card-lift">
                                        <CardContent className="p-6">
                                            <Icon className="h-6 w-6 text-[hsl(var(--primary))] mb-3" />
                                            <div className="font-display font-semibold text-lg mb-1">
                                                {f.title}
                                            </div>
                                            <p className="text-sm text-muted-foreground leading-relaxed">
                                                {f.body}
                                            </p>
                                        </CardContent>
                                    </Card>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            <section className="section-pad">
                <div className="container-page">
                    <Card className="bg-[hsl(var(--primary))] text-primary-foreground border-none">
                        <CardContent className="p-8 sm:p-12 grid md:grid-cols-2 gap-6 items-center">
                            <div>
                                <div className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--accent))] mb-2">
                                    Corporate Employers
                                </div>
                                <h3 className="font-display text-2xl sm:text-3xl font-semibold leading-tight">
                                    Hire from our talent pool. Fund our placement drives.
                                </h3>
                                <p className="mt-3 text-white/85 text-sm leading-relaxed max-w-lg">
                                    Get in touch to add your open roles to our upcoming job
                                    fairs, or partner as a CSR sponsor of our employment
                                    programs.
                                </p>
                            </div>
                            <div className="flex flex-col sm:flex-row md:justify-end gap-3">
                                <Button
                                    onClick={() => navigate('/contact')}
                                    className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))] hover:bg-[hsl(var(--accent))]/90 font-semibold"
                                    data-testid="job-portal-employer-contact"
                                >
                                    Contact Employer Team <ArrowRight className="h-4 w-4 ml-1.5" />
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={() => navigate('/csr-projects')}
                                    className="bg-transparent border-white/40 text-white hover:bg-white/10 hover:text-white"
                                >
                                    See CSR Projects
                                </Button>
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </section>
        </div>
    );
}
